import React, { useEffect, useMemo, useState } from "react";
import ImageUploadField from "../../../components/ImageUploadField";
import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const cardStyle = {
  background: "white",
  border: "1px solid #e5e7eb",
  borderRadius: "12px",
  padding: "20px",
  marginBottom: "20px",
};

const labelStyle = {
  fontSize: "14px",
  fontWeight: 600,
  color: "#111827",
};

const descStyle = {
  fontSize: "12px",
  color: "#6b7280",
  marginTop: "4px",
};

const rowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "14px",
  padding: "10px 0",
  borderBottom: "1px solid #f3f4f6",
};

const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    style={{
      width: "52px",
      height: "30px",
      border: "none",
      borderRadius: "999px",
      cursor: "pointer",
      background: checked ? "#2563eb" : "#d1d5db",
      position: "relative",
      transition: "background 0.2s ease",
      flexShrink: 0,
    }}
    aria-label="Toggle setting"
  >
    <span
      style={{
        position: "absolute",
        top: "3px",
        left: checked ? "25px" : "3px",
        width: "24px",
        height: "24px",
        borderRadius: "50%",
        background: "white",
        transition: "left 0.2s ease",
      }}
    />
  </button>
);

const HomepageSettings = ({
  settings,
  updateSettings,
  saveSettings,
  saving,
  hasUnsavedChanges,
  resetSettings,
  setMessage,
}) => {
  const [onboardingMode, setOnboardingMode] = useState(null);
  const [onboardingLoading, setOnboardingLoading] = useState(true);
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [keepAutoControl, setKeepAutoControl] = useState(false);
  const [radiusOptionsDraft, setRadiusOptionsDraft] = useState("");
  const defaultProductsHeroSlides = [
    {
      id: "slide-1",
      imageUrl: "",
      title: "Discover Beauty That Performs",
      subtitle: "Platform-curated products trusted by verified professionals",
      ctaLabel: "",
      ctaUrl: "",
      isActive: true,
      position: 1,
    },
    {
      id: "slide-2",
      imageUrl: "",
      title: "From Routine To Results",
      subtitle: "Find high-performing essentials for hair, skin, and wellness",
      ctaLabel: "",
      ctaUrl: "",
      isActive: true,
      position: 2,
    },
    {
      id: "slide-3",
      imageUrl: "",
      title: "New Season, New Favorites",
      subtitle: "Fresh picks, standout formulas, and must-have upgrades",
      ctaLabel: "",
      ctaUrl: "",
      isActive: true,
      position: 3,
    },
    {
      id: "slide-4",
      imageUrl: "",
      title: "Clean Beauty, Real Results",
      subtitle: "Browse clean formulations selected for quality and performance",
      ctaLabel: "",
      ctaUrl: "",
      isActive: true,
      position: 4,
    },
    {
      id: "slide-5",
      imageUrl: "",
      title: "Editor Picks This Week",
      subtitle: "Trending products chosen by platform editors and top stylists",
      ctaLabel: "",
      ctaUrl: "",
      isActive: true,
      position: 5,
    },
  ];


  const fetchOnboardingMode = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/platform-settings/onboarding-mode/public`,
      );
      setOnboardingMode(response.data?.onboardingMode || null);
    } catch (error) {
      if (setMessage) {
        setMessage({
          type: "error",
          text: "Failed to load onboarding mode status",
        });
      }
    } finally {
      setOnboardingLoading(false);
    }
  };

  useEffect(() => {
    fetchOnboardingMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const intervalLabel = useMemo(() => {
    const minutes = Number(onboardingMode?.checkIntervalMinutes || 30);
    if (minutes >= 7 * 24 * 60) return "Weekly";
    if (minutes >= 24 * 60) return "Daily";
    return `Every ${minutes} minutes`;
  }, [onboardingMode?.checkIntervalMinutes]);

  const tierLabel = useMemo(() => {
    const count = Number(onboardingMode?.lastQualifyingCount || 0);
    if (count >= 100) return "100+ (cron disabled)";
    if (count >= 50) return "50-99 (weekly checks)";
    if (count >= 10) return "10-49 (daily checks)";
    if (count >= 1) return "1-9 (30-min checks)";
    return "0 (30-min checks)";
  }, [onboardingMode?.lastQualifyingCount]);

  const runManualOverride = async (enabledValue) => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      if (setMessage) {
        setMessage({ type: "error", text: "Not authenticated" });
      }
      return;
    }

    try {
      setOverrideSaving(true);
      await axios.put(
        `${API_BASE_URL}/api/platform-settings/onboarding-mode/manual`,
        {
          enabled: enabledValue,
          reason: overrideReason,
          autoControlEnabled: keepAutoControl,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (setMessage) {
        setMessage({
          type: "success",
          text: `Onboarding card manually turned ${enabledValue ? "ON" : "OFF"}`,
        });
      }

      await fetchOnboardingMode();
      setOverrideReason("");
    } catch (error) {
      if (setMessage) {
        setMessage({
          type: "error",
          text:
            error.response?.data?.error ||
            "Failed to apply onboarding manual override",
        });
      }
    } finally {
      setOverrideSaving(false);
    }
  };

  const homepageSettings = settings?.homepageSettings || {
    rankingEngineEnabled: true,
    nearMeSectionEnabled: true,
    noDupEnabled: true,
    diversityEnabled: true,
    productPickupFilterEnabled: false,
    productPickupRadiusMiles: 25,
    publicSearchRadiusOptions: [5, 10, 25, 50],
    publicSearchDefaultRadiusMiles: 10,
    addressAutocompleteCountryCode: "US",
  };

  const parsedSearchRadiusOptions = (
    Array.isArray(homepageSettings.publicSearchRadiusOptions)
      ? homepageSettings.publicSearchRadiusOptions
      : [5, 10, 25, 50]
  )
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value >= 1 && value <= 200)
    .filter((value, index, arr) => arr.indexOf(value) === index)
    .sort((a, b) => a - b);

  const searchRadiusOptions =
    parsedSearchRadiusOptions.length > 0
      ? parsedSearchRadiusOptions
      : [5, 10, 25, 50];

  const searchRadiusOptionsText = searchRadiusOptions.join(", ");

  const searchDefaultRadius = searchRadiusOptions.includes(
    Number(homepageSettings.publicSearchDefaultRadiusMiles),
  )
    ? Number(homepageSettings.publicSearchDefaultRadiusMiles)
    : searchRadiusOptions[0];

  useEffect(() => {
    setRadiusOptionsDraft(searchRadiusOptionsText);
  }, [searchRadiusOptionsText]);

  const updateHomepageSettings = (patch) => {
    updateSettings({
      homepageSettings: {
        ...homepageSettings,
        ...patch,
      },
    });
  };

  const productsHero = settings?.productsPageHero || {
    enabled: true,
    autoRotateMs: 4500,
    slides: defaultProductsHeroSlides,
  };


  const currentHeroSlides =
    Array.isArray(productsHero.slides) && productsHero.slides.length > 0
      ? [...productsHero.slides].slice(0, 5)
      : defaultProductsHeroSlides;

  while (currentHeroSlides.length < 5) {
    const index = currentHeroSlides.length;
    currentHeroSlides.push({
      ...defaultProductsHeroSlides[index],
      id: `slide-${index + 1}`,
      position: index + 1,
    });
  }

  const updateProductsHeroField = (field, value) => {
    updateSettings({
      productsPageHero: {
        ...productsHero,
        [field]: value,
      },
    });
  };

  const updateHeroSlideField = (index, field, value) => {
    const slides = [...currentHeroSlides];
    slides[index] = {
      ...slides[index],
      [field]: value,
      id: slides[index]?.id || `slide-${index + 1}`,
      position: index + 1,
    };

    updateProductsHeroField("slides", slides);
  };


  const applyRadiusOptionsDraft = () => {
    const parsed = radiusOptionsDraft
      .split(",")
      .map((value) => Number(value.trim()))
      .filter((value) => Number.isFinite(value) && value >= 1 && value <= 200)
      .filter((value, index, arr) => arr.indexOf(value) === index)
      .sort((a, b) => a - b);

    if (parsed.length === 0) {
      if (setMessage) {
        setMessage({
          type: "error",
          text: "Enter at least one valid radius between 1 and 200 miles.",
        });
      }
      setRadiusOptionsDraft(searchRadiusOptionsText);
      return;
    }

    updateHomepageSettings({
      publicSearchRadiusOptions: parsed,
      publicSearchDefaultRadiusMiles: parsed.includes(searchDefaultRadius)
        ? searchDefaultRadius
        : parsed[0],
    });
    setRadiusOptionsDraft(parsed.join(", "));
  };

  return (
    <div>
      <div style={cardStyle}>
        <h3 style={{ margin: "0 0 8px", fontSize: "18px" }}>🏠 Homepage Controls</h3>
        <p style={{ margin: "0", color: "#6b7280", fontSize: "14px" }}>
          Toggle homepage ranking and merchandising behavior without redeploying.
        </p>

        <div style={{ marginTop: "16px" }}>
          <div style={rowStyle}>
            <div>
              <div style={labelStyle}>Ranking Engine</div>
              <div style={descStyle}>Master switch for homepage ranking logic.</div>
            </div>
            <Toggle
              checked={!!homepageSettings.rankingEngineEnabled}
              onChange={(value) =>
                updateHomepageSettings({ rankingEngineEnabled: value })
              }
            />
          </div>

          <div style={rowStyle}>
            <div>
              <div style={labelStyle}>Near Me Section</div>
              <div style={descStyle}>Enable location-driven homepage section.</div>
            </div>
            <Toggle
              checked={!!homepageSettings.nearMeSectionEnabled}
              onChange={(value) =>
                updateHomepageSettings({ nearMeSectionEnabled: value })
              }
            />
          </div>

          <div style={rowStyle}>
            <div>
              <div style={labelStyle}>No-Duplicate Enforcement</div>
              <div style={descStyle}>Avoid repeating the same entities across sections.</div>
            </div>
            <Toggle
              checked={!!homepageSettings.noDupEnabled}
              onChange={(value) => updateHomepageSettings({ noDupEnabled: value })}
            />
          </div>

          <div style={rowStyle}>
            <div>
              <div style={labelStyle}>Diversity Rules</div>
              <div style={descStyle}>Balance city/business variety in section ordering.</div>
            </div>
            <Toggle
              checked={!!homepageSettings.diversityEnabled}
              onChange={(value) =>
                updateHomepageSettings({ diversityEnabled: value })
              }
            />
          </div>

          <div style={rowStyle}>
            <div>
              <div style={labelStyle}>Pickup Product Filter</div>
              <div style={descStyle}>Restrict homepage products to pickup-feasible distance.</div>
            </div>
            <Toggle
              checked={!!homepageSettings.productPickupFilterEnabled}
              onChange={(value) =>
                updateHomepageSettings({ productPickupFilterEnabled: value })
              }
            />
          </div>

          <div style={{ ...rowStyle, borderBottom: "none" }}>
            <div>
              <div style={labelStyle}>Pickup Radius (miles)</div>
              <div style={descStyle}>Applied when pickup product filter is enabled.</div>
            </div>
            <input
              type="number"
              min="1"
              max="100"
              value={homepageSettings.productPickupRadiusMiles || 25}
              onChange={(e) =>
                updateHomepageSettings({
                  productPickupRadiusMiles: Math.max(
                    1,
                    Math.min(100, Number(e.target.value) || 25),
                  ),
                })
              }
              style={{
                width: "100px",
                padding: "8px 10px",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                fontSize: "14px",
              }}
            />
          </div>

          <div style={rowStyle}>
            <div>
              <div style={labelStyle}>Public Search Radius Options</div>
              <div style={descStyle}>
                Comma-separated miles shown in both /shops and /freelancers radius dropdowns.
              </div>
            </div>
            <input
              type="text"
              value={radiusOptionsDraft}
              onChange={(e) => setRadiusOptionsDraft(e.target.value)}
              onBlur={applyRadiusOptionsDraft}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyRadiusOptionsDraft();
                }
              }}
              style={{
                width: "180px",
                padding: "8px 10px",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                fontSize: "14px",
              }}
            />
          </div>

          <div style={{ ...rowStyle, borderBottom: "none" }}>
            <div>
              <div style={labelStyle}>Public Search Default Radius</div>
              <div style={descStyle}>Default radius selected on /shops and /freelancers.</div>
            </div>
            <select
              value={String(searchDefaultRadius)}
              onChange={(e) =>
                updateHomepageSettings({
                  publicSearchDefaultRadiusMiles: Number(e.target.value),
                })
              }
              style={{
                width: "180px",
                padding: "8px 10px",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                fontSize: "14px",
                background: "white",
              }}
            >
              {searchRadiusOptions.map((option) => (
                <option key={option} value={String(option)}>
                  {option} miles
                </option>
              ))}
            </select>
          </div>

          <div style={{ ...rowStyle, borderBottom: "none" }}>
            <div>
              <div style={labelStyle}>Address Autocomplete Country Scope</div>
              <div style={descStyle}>
                Two-letter country code used by street and city autocomplete (example: US, CA, GB).
              </div>
            </div>
            <input
              type="text"
              maxLength="2"
              value={String(homepageSettings.addressAutocompleteCountryCode || "US").toUpperCase()}
              onChange={(e) =>
                updateHomepageSettings({
                  addressAutocompleteCountryCode: String(e.target.value || "US")
                    .replace(/[^a-z]/gi, "")
                    .slice(0, 2)
                    .toUpperCase(),
                })
              }
              style={{
                width: "80px",
                padding: "8px 10px",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                fontSize: "14px",
              }}
            />
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <h3 style={{ margin: "0 0 8px", fontSize: "18px" }}>
          🎯 Global Products Hero (Platform-Owned)
        </h3>
        <p style={{ margin: "0", color: "#6b7280", fontSize: "14px" }}>
          Configure hero slides for the global products page. This content is platform-managed and does not use seller product images.
        </p>

        <div style={{ marginTop: "16px", ...rowStyle }}>
          <div>
            <div style={labelStyle}>Enable Global Products Hero</div>
            <div style={descStyle}>Master switch for showing hero slides on /products.</div>
          </div>
          <Toggle
            checked={productsHero.enabled !== false}
            onChange={(value) => updateProductsHeroField("enabled", value)}
          />
        </div>

        <div style={{ ...rowStyle, borderBottom: "none" }}>
          <div>
            <div style={labelStyle}>Auto-Rotate Interval (ms)</div>
            <div style={descStyle}>Rotation speed for the products hero carousel.</div>
          </div>
          <input
            type="number"
            min="2000"
            max="20000"
            step="100"
            value={productsHero.autoRotateMs ?? 4500}
            onChange={(e) =>
              updateProductsHeroField(
                "autoRotateMs",
                parseInt(e.target.value || "4500", 10),
              )
            }
            style={{
              width: "180px",
              padding: "8px 10px",
              borderRadius: "8px",
              border: "1px solid #d1d5db",
              fontSize: "14px",
            }}
          />
        </div>

        <div style={{ display: "grid", gap: "16px", marginTop: "14px" }}>
          {currentHeroSlides.slice(0, 5).map((slide, index) => (
            <div
              key={slide.id || `hero-slide-${index}`}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "10px",
                padding: "16px",
                background: "#fafafa",
              }}
            >
              <h4 style={{ margin: "0 0 12px", fontSize: "15px", fontWeight: 600 }}>
                Slide {index + 1}
              </h4>

              <div style={{ display: "grid", gap: "12px" }}>
                <div>
                  <ImageUploadField
                    variant="inline"
                    label="Background Image (optional)"
                    buttonLabel={slide.imageUrl ? "Replace image" : "Upload image"}
                    endpoint={`${API_BASE_URL}/api/admin/settings/products-hero-image`}
                    fieldName="image"
                    showPreviews={false}
                    onChange={(url) =>
                      updateHeroSlideField(index, "imageUrl", url)
                    }
                    onError={(message) =>
                      setMessage && setMessage({ type: "error", text: message })
                    }
                  />
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                    {!!slide.imageUrl && (
                      <button
                        type="button"
                        onClick={() => updateHeroSlideField(index, "imageUrl", "")}
                        style={{
                          border: "1px solid #fecaca",
                          background: "#fff1f2",
                          color: "#be123c",
                          borderRadius: "6px",
                          padding: "6px 10px",
                          fontSize: "12px",
                          cursor: "pointer",
                        }}
                      >
                        Remove Image
                      </button>
                    )}
                  </div>
                  {!!slide.imageUrl && (
                    <div style={{ marginTop: "8px" }}>
                      <img
                        src={slide.imageUrl}
                        alt={`Slide ${index + 1} preview`}
                        style={{
                          width: "100%",
                          maxWidth: "320px",
                          height: "120px",
                          objectFit: "cover",
                          borderRadius: "8px",
                          border: "1px solid #d1d5db",
                        }}
                      />
                    </div>
                  )}
                </div>

                <label style={{ display: "block" }}>
                  <span style={{ display: "block", fontSize: "13px", fontWeight: 500, marginBottom: "6px" }}>
                    Headline
                  </span>
                  <input
                    type="text"
                    maxLength="120"
                    value={slide.title || ""}
                    onChange={(e) => updateHeroSlideField(index, "title", e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                    }}
                  />
                </label>

                <label style={{ display: "block" }}>
                  <span style={{ display: "block", fontSize: "13px", fontWeight: 500, marginBottom: "6px" }}>
                    Subtext
                  </span>
                  <textarea
                    rows={2}
                    maxLength="220"
                    value={slide.subtitle || ""}
                    onChange={(e) => updateHeroSlideField(index, "subtitle", e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                      resize: "vertical",
                    }}
                  />
                </label>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                  <label style={{ display: "block" }}>
                    <span style={{ display: "block", fontSize: "13px", fontWeight: 500, marginBottom: "6px" }}>
                      CTA Label (optional)
                    </span>
                    <input
                      type="text"
                      maxLength="40"
                      value={slide.ctaLabel || ""}
                      onChange={(e) => updateHeroSlideField(index, "ctaLabel", e.target.value)}
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: "14px",
                      }}
                    />
                  </label>
                  <label style={{ display: "block" }}>
                    <span style={{ display: "block", fontSize: "13px", fontWeight: 500, marginBottom: "6px" }}>
                      CTA URL (optional)
                    </span>
                    <input
                      type="text"
                      maxLength="260"
                      value={slide.ctaUrl || ""}
                      onChange={(e) => updateHeroSlideField(index, "ctaUrl", e.target.value)}
                      placeholder="/products or https://..."
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: "14px",
                      }}
                    />
                  </label>
                </div>

                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={slide.isActive !== false}
                    onChange={(e) => updateHeroSlideField(index, "isActive", e.target.checked)}
                    style={{ width: "16px", height: "16px" }}
                  />
                  Slide active
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={cardStyle}>
        <h3 style={{ margin: "0 0 8px", fontSize: "18px" }}>
          🚦 Onboarding Card Auto-Control
        </h3>
        <p style={{ margin: "0", color: "#6b7280", fontSize: "14px" }}>
          Controls whether the homepage onboarding card can appear. Auto mode turns it off when at least one global R1/R4/R7 account is live and visible.
        </p>

        {onboardingLoading ? (
          <p style={{ marginTop: "14px", color: "#6b7280" }}>Loading onboarding status...</p>
        ) : (
          <>
            <div style={{ marginTop: "16px", background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: "10px", padding: "12px" }}>
              <div style={{ fontSize: "13px", color: "#111827", lineHeight: 1.7 }}>
                <div><strong>Current Card State:</strong> {onboardingMode?.enabled === false ? "OFF" : "ON"}</div>
                <div><strong>Auto Control:</strong> {onboardingMode?.autoControlEnabled === false ? "Disabled" : "Enabled"}</div>
                <div><strong>Cron:</strong> {onboardingMode?.cronEnabled === false ? "Disabled" : "Enabled"}</div>
                <div><strong>Qualifying Accounts:</strong> {onboardingMode?.lastQualifyingCount || 0}</div>
                <div><strong>Current Tier:</strong> {tierLabel}</div>
                <div><strong>Check Cadence:</strong> {intervalLabel}</div>
              </div>
            </div>

            <div style={{ marginTop: "14px" }}>
              <label style={{ ...labelStyle, display: "block", marginBottom: "6px" }}>
                Override Reason (optional)
              </label>
              <input
                type="text"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="Example: launch campaign in this environment"
                style={{
                  width: "100%",
                  maxWidth: "560px",
                  padding: "9px 10px",
                  borderRadius: "8px",
                  border: "1px solid #d1d5db",
                  fontSize: "14px",
                }}
              />
            </div>

            <div style={{ marginTop: "12px", display: "flex", alignItems: "center", gap: "10px" }}>
              <input
                id="keep-auto-control"
                type="checkbox"
                checked={keepAutoControl}
                onChange={(e) => setKeepAutoControl(e.target.checked)}
              />
              <label htmlFor="keep-auto-control" style={{ fontSize: "13px", color: "#374151" }}>
                Keep auto-control enabled after manual override
              </label>
            </div>

            <div style={{ display: "flex", gap: "10px", marginTop: "14px", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => runManualOverride(true)}
                disabled={overrideSaving}
                style={{
                  padding: "10px 14px",
                  borderRadius: "8px",
                  border: "none",
                  background: overrideSaving ? "#9ca3af" : "#059669",
                  color: "white",
                  fontWeight: 600,
                  cursor: overrideSaving ? "not-allowed" : "pointer",
                }}
              >
                Force ON
              </button>

              <button
                type="button"
                onClick={() => runManualOverride(false)}
                disabled={overrideSaving}
                style={{
                  padding: "10px 14px",
                  borderRadius: "8px",
                  border: "none",
                  background: overrideSaving ? "#9ca3af" : "#dc2626",
                  color: "white",
                  fontWeight: 600,
                  cursor: overrideSaving ? "not-allowed" : "pointer",
                }}
              >
                Force OFF
              </button>

              <button
                type="button"
                onClick={fetchOnboardingMode}
                disabled={overrideSaving}
                style={{
                  padding: "10px 14px",
                  borderRadius: "8px",
                  border: "1px solid #d1d5db",
                  background: "white",
                  color: "#374151",
                  fontWeight: 600,
                  cursor: overrideSaving ? "not-allowed" : "pointer",
                }}
              >
                Refresh Status
              </button>
            </div>
          </>
        )}
      </div>

      <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
        <button
          onClick={() => saveSettings()}
          disabled={saving || !hasUnsavedChanges}
          style={{
            padding: "10px 20px",
            borderRadius: "8px",
            border: "none",
            background: saving || !hasUnsavedChanges ? "#9ca3af" : "#2563eb",
            color: "white",
            fontWeight: 600,
            cursor: saving || !hasUnsavedChanges ? "not-allowed" : "pointer",
          }}
        >
          {saving ? "Saving..." : "Save Homepage Settings"}
        </button>

        <button
          onClick={resetSettings}
          disabled={saving || !hasUnsavedChanges}
          style={{
            padding: "10px 20px",
            borderRadius: "8px",
            border: "1px solid #d1d5db",
            background: "white",
            color: "#374151",
            fontWeight: 600,
            cursor: saving || !hasUnsavedChanges ? "not-allowed" : "pointer",
            opacity: saving || !hasUnsavedChanges ? 0.5 : 1,
          }}
        >
          Reset
        </button>
      </div>
    </div>
  );
};

export default HomepageSettings;
